import React from "react";
import PropTypes from "prop-types";
import PayPalLogo from "components/v3/paypal-logo/PayPalLogo";
import Button, { BUTTON_THEME_LINK } from "components/v3/button/Button";
import PaymentForm from "containers/v3/PaymentFormContainer";
import InvisibleRecaptcha from "components/v3/recaptcha/InvisibleRecaptcha";
import PrimaryButton from "components/v3/primary-button/PrimaryButton";
import { THINKING_TYPE_LOADING } from "common/constants/v2/Constants";
import ErrorHandler from "components/v3/error-handler/ErrorHandler";
import SubmitError from "components/v3/submit-error/SubmitError";
import styles from "./styles.module.scss";

const LoadingMessage = () => {
    return (
        <div className={ styles.loading }>Loading your payment method...</div>
    );
};

class PaymentDetails extends React.Component {
    constructor(props) {
        super(props);

        this._onEditPayment = this._onEditPayment.bind(this);
        this._onCaptchaResponse = this._onCaptchaResponse.bind(this);
    }

    _onEditPayment() {
        this.props.editPayment();
    }

    _onCaptchaResponse(grecaptchaToken) {
        this.props.submitPayment(this.props.subscription, grecaptchaToken);
    }

    render() {
        const {
            grecaptchaApiKey,
            paymentMethod: {
                creditCard,
                paypal = {}
            },
            thinking,
            isEditingPayment,
            subscription,
            errors
        } = this.props;

        if (!creditCard && !paypal.baid) {
            return null;
        }

        if (thinking === THINKING_TYPE_LOADING) {
            return <LoadingMessage />;
        }

        const editPaymentLabel = isEditingPayment ? "Cancel" : "Change Payment Method";

        return (
            <div className={ styles.paymentDetails }>
                { !isEditingPayment && <PaymentInformation cardInformation={ creditCard } paypalId={ paypal.baid } /> }
                { isEditingPayment && (
                    <div className={ styles.paymentForm }>
                        <ErrorHandler type={ "unaccepted_payment_method" } errors={ errors }>
                            <SubmitError />
                        </ErrorHandler>
                        <EditPaymentForm
                            subscription={ subscription }
                            grecaptchaApiKey={ grecaptchaApiKey }
                            onCaptchaResponse={ this._onCaptchaResponse }
                            thinking={ thinking }
                        />
                    </div>
                ) }
                {
                    !paypal.baid && (
                        <div className={ styles.editPayment }>
                            <Button
                                text={ editPaymentLabel }
                                onClick={ this._onEditPayment }
                                theme={ BUTTON_THEME_LINK }
                            />
                        </div>
                    )
                }
            </div>
        );
    }
}

const EditPaymentForm = ({ subscription, grecaptchaApiKey, onCaptchaResponse, thinking }) => {
    return (
        <InvisibleRecaptcha grecaptchaApiKey={ grecaptchaApiKey } onCaptchaResponse={ onCaptchaResponse }>
            <PaymentForm showExistingPayment={ false } subscription={ subscription } />
            <div className={ styles.nextButton }>
                <PrimaryButton text="Next" thinking={ thinking } />
            </div>
        </InvisibleRecaptcha>
    );
};

const PaymentInformation = ({ cardInformation, paypalId }) => {
    if (paypalId) {
        return <PayPalInformation />;
    }

    const {
        brand,
        lastFourDigits
    } = cardInformation;

    if (!brand || !lastFourDigits) {
        return <div>Unable to load the payment method associated with this subscription.</div>;
    }

    return <CardInformation brand={ brand } lastFourDigits={ lastFourDigits } />;
};

const CardInformation = ({ brand, lastFourDigits }) => {
    return (
        <div>
            <div>Your payment method:</div>
            <div className={ styles.cardInformation }>
                <span className={ styles.brand }>{ brand } </span>
                <span className={ styles.cardNumber }>xxxx-xxxx-xxxx-{ lastFourDigits }</span>
            </div>
        </div>
    );
};

const PayPalInformation = () => {
    return (
        <div className={ styles.paymentInfo }>
            <PayPalLogo />
        </div>
    );
};

PaymentDetails.defaultProps = {
    isEditingPayment: false,
    errors: {}
};

PaymentDetails.propTypes = {
    editPayment: PropTypes.func.isRequired,
    isEditingPayment: PropTypes.bool,
    subscription: PropTypes.shape({
        id: PropTypes.string.isRequired
    }).isRequired,
    errors: PropTypes.object,
    submitPayment: PropTypes.func.isRequired,
    paymentMethod: PropTypes.shape({
        creditCard: PropTypes.shape({
            brand: PropTypes.string.isRequired,
            lastFourDigits: PropTypes.string.isRequired,
            expirationDate: PropTypes.string.isRequired
        }),
        paypalId: PropTypes.string
    }),
    thinking: PropTypes.string.isRequired,
    grecaptchaApiKey: PropTypes.string
};

export default PaymentDetails;
